import { Button, Loading } from '@brighterion/br-ui';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { BULK_UPLOAD_PROCESSING_PROMPT, handleOnClickEvent, handleKeyEvent } from 'Utils';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';
import {
    POSSIBLE_STATUS,
    toggleConfirmModal,
    updateStatus,
    useUploaderContext,
    useUploaderDispatchContext
} from './context';
import styles from './Uploader.css';

type Props = {
    isPCIList: boolean;
};

const Footer = React.forwardRef(({ isPCIList }: Props, ref) => {
    const refCurrent = (ref as React.RefObject<HTMLIFrameElement>)?.current;

    const { status, fileDetails } = useUploaderContext();

    const dispatch = useUploaderDispatchContext();
    const location = useLocation();
    const history = useHistory();
    const { t } = useTranslation();

    const enabled = status === POSSIBLE_STATUS.validFile;
    const isProcessing = status === POSSIBLE_STATUS.processingFile;
    const handlePreview = () => {
        trackEvent({
            eventType: EventTypes.track,
            eventName: EventNames.click,
            payload: {
                clickIdentifier: 'Lists - Bulk Upload Entries',
                text: 'Bulk Upload Entries'
            }
        });
        if (isPCIList) {
            if (!fileDetails.totalNumberOfRows || !fileDetails.totalChunks) {
                refCurrent?.contentWindow?.postMessage(
                    {
                        type: BULK_UPLOAD_PROCESSING_PROMPT
                    },
                    refCurrent?.src
                );
                return;
            }
        }

        // non PCI list - this means that we still need to process the file
        if (!isPCIList && !fileDetails.totalNumberOfRows) {
            dispatch(updateStatus(POSSIBLE_STATUS.processingFile));
            return;
        }
        // file has been processed before
        dispatch(updateStatus(POSSIBLE_STATUS.processingFileComplete));
        dispatch(toggleConfirmModal({ value: true, changeStatus: false }));
    };

    const returnToPreviousRoute = () => {
        const routeToGoTo = location.pathname.split('/').slice(0, 3).join('/');
        history.push(routeToGoTo);
    };

    if (status === POSSIBLE_STATUS.chunking || status === POSSIBLE_STATUS.chunkingComplete) return null;

    return (
        <div className={styles.buttonsRow} data-testid='list-bulk-upload-footer'>
            <Button
                variant='tertiary'
                size='small'
                data-testid='cancel-btn'
                onClick={handleOnClickEvent(returnToPreviousRoute)}
                onKeyDown={handleKeyEvent(returnToPreviousRoute)}
            >
                {t('Cancel')}
            </Button>
            <Button
                variant='primary'
                size='small'
                data-testid='upload-btn'
                onClick={handleOnClickEvent(handlePreview)}
                onKeyDown={handleKeyEvent(handlePreview)}
                disabled={!enabled || isProcessing}
            >
                {isProcessing ? <Loading size='smMd' fillName='white' /> : t('Preview File')}
            </Button>
        </div>
    );
});

export default Footer;
